// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - AdminCreditsPanel
// Panel admin pour octroyer des crédits aux utilisateurs (testeurs)
// Accessible depuis le Cockpit Pro ou Cockpit Particulier
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Coins, Gift, Search, Loader2, Check, AlertCircle,
  Users, Crown, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer l'userId depuis différentes sources
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  const userId = localStorage.getItem('userId');
  
  if (userId) return userId;
  
  if (ecosystemUser) {
    try {
      const parsed = JSON.parse(ecosystemUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (buyerUser) {
    try {
      const parsed = JSON.parse(buyerUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (clientUser) {
    try {
      const parsed = JSON.parse(clientUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  return null;
};

export default function AdminCreditsPanel({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [grantAmount, setGrantAmount] = useState(100);
  const [grantReason, setGrantReason] = useState('Test bêta');
  const [granting, setGranting] = useState(false);
  
  const adminId = getUserId();
  
  // Récupérer la liste des utilisateurs avec crédits
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/credits/admin/users-with-credits?admin_id=${adminId}`);
        if (response.ok) {
          const data = await response.json();
          setUsers(data.users || []);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        toast.error('Erreur lors de la récupération des utilisateurs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [isOpen, adminId]);
  
  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.user_id?.toLowerCase().includes(query) ||
      user.user_info?.email?.toLowerCase().includes(query) ||
      user.user_info?.name?.toLowerCase().includes(query)
    );
  });
  
  // Octroyer des crédits
  const handleGrant = async () => {
    if (!selectedUser || !grantAmount || grantAmount <= 0) {
      toast.error('Sélectionne un utilisateur et entre un montant valide');
      return;
    }
    
    setGranting(true);
    try {
      const response = await fetch(`${API_URL}/api/credits/admin/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          amount: grantAmount,
          reason: grantReason,
          admin_id: adminId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${grantAmount} crédits ajoutés à ${selectedUser.user_info?.email || selectedUser.user_id}`);
        
        // Mettre à jour la liste locale
        setUsers(prev => prev.map(u => 
          u.user_id === selectedUser.user_id
            ? { ...u, credits_purchased: (u.credits_purchased || 0) + grantAmount }
            : u
        ));
        
        setSelectedUser(null);
        setGrantAmount(100);
        setGrantReason('Test bêta');
      } else {
        toast.error(data.detail || 'Erreur lors de l\'ajout des crédits');
      }
    } catch (err) {
      console.error('Error granting credits:', err);
      toast.error('Erreur réseau');
    } finally {
      setGranting(false);
    }
  };
  
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-5 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Admin Crédits</h2>
                <p className="text-white/80 text-sm">Offrir des crédits aux testeurs</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par email ou ID..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>
            
            {/* Liste des utilisateurs */}
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Aucun utilisateur trouvé
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const total = (user.credits_subscription || 0) + (user.credits_purchased || 0);
                  const isSelected = selectedUser?.user_id === user.user_id;
                  
                  return (
                    <button
                      key={user.user_id}
                      onClick={() => setSelectedUser(user)}
                      className={`
                        w-full flex items-center justify-between p-3 text-left
                        transition-colors hover:bg-gray-50
                        ${isSelected ? 'bg-amber-50 border-l-2 border-l-amber-500' : ''}
                      `}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {user.user_info?.email || user.user_id}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user.user_info?.name || 'Utilisateur'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Coins className="w-4 h-4 text-amber-500" />
                        <span className="font-semibold text-gray-700">{total}</span>
                        {isSelected && <Check className="w-4 h-4 text-amber-600" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            
            {/* Formulaire d'ajout */}
            {selectedUser && (
              <div className="bg-amber-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2 text-amber-800">
                  <Gift className="w-4 h-4" />
                  <span className="font-medium text-sm">
                    Offrir des crédits à {selectedUser.user_info?.email || selectedUser.user_id}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Montant
                    </label>
                    <input
                      type="number"
                      value={grantAmount}
                      onChange={(e) => setGrantAmount(parseInt(e.target.value) || 0)}
                      min={1}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Raison
                    </label>
                    <input
                      type="text"
                      value={grantReason}
                      onChange={(e) => setGrantReason(e.target.value)}
                      placeholder="Test bêta..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleGrant}
                  disabled={granting || grantAmount <= 0}
                  className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {granting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Gift className="w-4 h-4" />
                      Offrir {grantAmount} crédits
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Raccourcis */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Montants rapides</p>
              <div className="flex gap-2 flex-wrap">
                {[50, 100, 250, 500, 1000].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setGrantAmount(amount)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                      ${grantAmount === amount 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-amber-300'
                      }
                    `}
                  >
                    {amount}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
